import React from 'react';
import { CustomerAddress } from '../../../../types/customers';
import Modal from '../../../../components/Modal';
import { Input, ValidationState } from '../../../../components/Forms';
import { Button, ButtonGroup } from '../../../../components/Buttons';
import { Formik } from 'formik';
import CountryDropdown from './CountryDropdown';
import AddressTypeDropdown from './AddressTypeDropdown';
import { WithTranslation, withTranslation } from 'react-i18next';
import addressValidations from './addressValidations';

interface AddressModalProps extends WithTranslation {
    accountId: string;
    customerId: string;
    closeModal: () => void;
    saveAddresses: (values: CustomerAddress) => void;
}

const AddAddressModal = (
    {
        closeModal,
        saveAddresses,
        t,
    }: AddressModalProps
) => {
    const initialValues: CustomerAddress =  {
        address_line: '',
        postal_place: '',
        postal_code: '',
        country: 'NO',
        address_line_2: '',
        type:  'home',
        custom_type:  '',
        comment: '',
    };

    return (
        <Modal>
            <Formik
                initialValues={initialValues as CustomerAddress}
                onSubmit={(values, { setSubmitting }) => {
                    saveAddresses(values);
                    setSubmitting(false);
                }}
                validate={addressValidations}
            >
                {({ values, errors, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue, touched, setTouched }) => (
                    <form onSubmit={handleSubmit}>
                        <Input
                            autoComplete="off"
                            label={t('customers.customer.addresses.form.fields.address_line')}
                            placeholder={t('customers.customer.addresses.form.fields.address_line')}
                            type="text"
                            name="address_line"
                            value={values.address_line}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validation={{
                                state:
                                    errors.address_line && touched.address_line
                                        ? ValidationState.Invalid
                                        : ValidationState.Pristine,
                                message: errors.address_line,
                            }}
                        />

                        <Input
                            autoComplete="off"
                            label={t('customers.customer.addresses.form.fields.address_line_2')}
                            placeholder={t('customers.customer.addresses.form.fields.address_line_2')}
                            type="text"
                            name="address_line_2"
                            value={values.address_line_2 || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <Input
                            autoComplete="off"
                            label={t('customers.customer.addresses.form.fields.postal_code')}
                            placeholder={t('customers.customer.addresses.form.fields.postal_code')}
                            type="text"
                            name="postal_code"
                            value={values.postal_code || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validation={{
                                state:
                                    errors.postal_code && touched.postal_code
                                        ? ValidationState.Invalid
                                        : ValidationState.Pristine,
                                message: errors.postal_code,
                            }}

                        />
                        <Input
                            autoComplete="off"
                            label={t('customers.customer.addresses.form.fields.postal_place')}
                            placeholder={t('customers.customer.addresses.form.fields.postal_place')}
                            type="text"
                            name="postal_place"
                            value={values.postal_place || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            validation={{
                                state:
                                    errors.postal_place && touched.postal_place
                                        ? ValidationState.Invalid
                                        : ValidationState.Pristine,
                                message: errors.postal_place,
                            }}
                        />

                        <CountryDropdown
                            name="country"
                            value={values.country || 'NO'}
                            label={t('customers.customer.addresses.form.fields.country')}
                            onChange={value => {
                                setFieldValue('country', value);
                                setTouched({ country: true });
                            }}
                            placeholder={t('customers.customer.addresses.form.fields.country')}
                            validation={{
                                state:
                                    errors.country && touched.country
                                        ? ValidationState.Invalid
                                        : ValidationState.Pristine,
                                message: errors.country,
                            }}
                        />

                        <AddressTypeDropdown
                            label={t('customers.customer.addresses.form.fields.type')}
                            placeholder={t('customers.customer.addresses.form.fields.type')}
                            name="type"
                            value={values.type || null}
                            onChange={value => {
                                setFieldValue('type', value);
                                if (value !== 'customer') setFieldValue('custom_type', '');
                            }}
                        />

                        {values.type === 'custom' && (
                            <Input
                                autoComplete="off"
                                label={t('customers.customer.addresses.form.fields.custom_type')}
                                placeholder={t('customers.customer.addresses.form.fields.custom_type')}
                                type="text"
                                name="custom_type"
                                value={values.custom_type || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        )}

                        <Input
                            autoComplete="off"
                            label={t('customers.customer.addresses.form.fields.comment')}
                            placeholder={t('customers.customer.addresses.form.fields.comment')}
                            type="text"
                            name="comment"
                            value={values.comment || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <ButtonGroup>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {t('customers.customer.addresses.form.fields.submit')}
                            </Button>

                            <Button
                                className="alt"
                                type="button"
                                onClick={() => closeModal()}
                            >
                                {t('customers.customer.addresses.form.fields.close')}
                            </Button>
                        </ButtonGroup>
                    </form>
                )}
            </Formik>
        </Modal>
    );
};

export default withTranslation()(AddAddressModal);
